/**
 * Customer-facing presentation data shared by planner Steps and modules.
 *
 * Text that the backend, admin and order emails also use (titles, subtitles,
 * features) stays in `@planner/config` and is composed here, so there is one
 * maintained copy. Prices come from the catalogue and manufacturing rules
 * from `doorRules`; neither belongs in this file.
 */
import {
  doorColours,
  doorCopy,
  doorPreviewColour,
  doorTypeIds,
  type DoorTypeId,
  type MeasurementMode,
} from '@planner/config';
import type { LockSide } from '@planner/shared/doors';
import barrierPhoto from './src/assets/doors/barrier-black.webp';
import exactArtwork from './src/assets/doors/measurement-exact.svg';
import openingArtwork from './src/assets/doors/measurement-opening.svg';
import securityPhoto from './src/assets/doors/security-monument.webp';
import exactGuide from './src/assets/guides/ProtectorAl_Enter_Exact_Door_Size.pdf?url';
import openingGuide from './src/assets/guides/ProtectorAl_Security_Door_Measuring_Guide.pdf?url';

export interface DoorTypePresentation {
  id: DoorTypeId;
  title: string;
  subtitle: string;
  features: readonly string[];
  /** Fixed product photo for the Door type card, not a selected-colour preview. */
  image: { src: string; alt: string };
  badge?: string;
}

const extras = {
  barrier: { image: { src: barrierPhoto, alt: 'Barrier screen door in Black' } },
  security: {
    image: { src: securityPhoto, alt: 'Security screen door in Monument' },
    badge: 'Most popular',
  },
} satisfies Record<DoorTypeId, Pick<DoorTypePresentation, 'image' | 'badge'>>;

export const doorTypes: readonly DoorTypePresentation[] = doorTypeIds.map((id) => ({
  id,
  title: doorCopy[id].title,
  subtitle: doorCopy[id].subtitle,
  features: doorCopy[id].features,
  ...extras[id],
}));

const colourPhotoSources = import.meta.glob<string>(
  ['./src/assets/doors/barrier-*.webp', './src/assets/doors/security-*.webp'],
  { eager: true, query: '?url', import: 'default' },
);

function colourPhoto(type: DoorTypeId, colour: (typeof doorColours)[number]) {
  const path = `./src/assets/doors/${type}-${colour.renderSlug}.webp`;
  const src = colourPhotoSources[path];
  if (!src) {
    throw new Error(`Missing ${type} photo for ${colour.name}: ${path}`);
  }
  return { src, alt: `${doorCopy[type].title} in ${colour.name}` };
}

export const colourPresentation = {
  defaultColour: doorPreviewColour,
  heading: 'Choose your frame colour',
  introduction: 'Powdercoated to match popular house colour themes.',
  hardwareNote: {
    barrier: 'The hinges, grille and handle are always Satin Black.',
    security: 'The hinges and handle are always Satin Black.',
  },
  selectedLabel: 'Selected colour',
  indicativeNote: 'Colours shown are indicative only.',
  unavailableLabel: 'Not available in this size',
  photoFacing: { barrier: 'left', security: 'right' } satisfies Record<DoorTypeId, LockSide>,
  colours: [...doorColours]
    .sort((first, second) => first.name.localeCompare(second.name))
    .map((colour) => ({
      ...colour,
      photos: {
        barrier: colourPhoto('barrier', colour),
        security: colourPhoto('security', colour),
      },
    })),
} as const;

export const reviewPresentation = {
  mesh: {
    barrier: 'Diamond Grille & Fibreglass Flyscreen',
    security: 'Woven 316 Stainless Steel',
  } satisfies Record<DoorTypeId, string>,
  hardware: 'Satin Black',
  cylinderHelp:
    "What's a 5-pin Euro cylinder? It's the key barrel that sits inside the lock. It has five pins inside that only line up with the right key, and the Euro shape is a standard size, so a locksmith can easily replace or re-key it if needed.",
  finalPriceNotice:
    'Final price will be confirmed by Bunnings Warehouse on check out or at the Special Orders Desk in your local Bunnings Warehouse store.',
} as const;

/** Selling points shown beneath the Door type cards. */
export const doorTypeValueStrip = [
  { id: 'made-to-order', title: 'Made to Order', body: 'Custom made in Australia' },
  {
    id: 'frame-colours',
    title: `${doorColours.length} Frame Colours`,
    body: 'Powder-coated aluminium',
  },
  { id: 'premium-aluminium', title: 'Premium Aluminium', body: 'Made to last' },
] as const;

export interface MeasurementModePresentation {
  title: string;
  description: string;
  heading: string;
  instructions: string;
  fieldsHeading: string;
  image: { src: string; alt: string; title: string };
  helpLabel: string;
  guideLabel: string;
  guideUrl: string;
}

export const measurementModeIds = ['diy', 'trade'] as const satisfies readonly MeasurementMode[];

export const measurementPresentation = {
  diy: {
    title: 'Measure my opening',
    description: 'Most homeowners choose this',
    heading: 'Measure the opening',
    instructions: 'This is the inside of the door frame.',
    fieldsHeading: 'Opening dimensions',
    image: {
      src: openingArtwork,
      alt: 'Opening measured at three widths and two heights.',
      title: 'Measure the opening — dimension guide',
    },
    helpLabel: 'Need help measuring?',
    guideLabel: 'Download PDF guide',
    guideUrl: openingGuide,
  },
  trade: {
    title: 'I have exact door dimensions',
    description: 'Manufactured to these exact measurements',
    heading: 'Enter the door size directly',
    instructions: 'Manufactured to these exact dimensions.',
    fieldsHeading: 'Door dimensions',
    image: {
      src: exactArtwork,
      alt: 'Finished door width and height.',
      title: 'Door size — dimension guide',
    },
    helpLabel: 'Need help with exact door sizes?',
    guideLabel: 'Download exact door size guide',
    guideUrl: exactGuide,
  },
} as const satisfies Record<MeasurementMode, MeasurementModePresentation>;
